import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, X, SlidersHorizontal, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import ProductCard from '@/components/ProductCard';
import { useScrollAnimation } from '@/hooks/useAnimations';
import { useSeo } from '@/hooks/useSeo';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Paginazione "a finestra": mostra prima/ultima pagina + intorno alla corrente, con ellissi.
function getPageItems(current, total, delta = 1) {
  const range = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }
  const result = [];
  let prev;
  for (const i of range) {
    if (prev) {
      if (i - prev === 2) result.push(prev + 1);
      else if (i - prev > 2) result.push('…');
    }
    result.push(i);
    prev = i;
  }
  return result;
}

export default function CatalogPage() {
  useSeo({
    title: 'Collezione Gioielli',
    description:
      "Sfoglia la collezione Ciro Gallotti: anelli, collane, bracciali e orecchini artigianali in oro, diamanti e pietre preziose. Filtra per categoria e materiale.",
    path: '/collezione',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://cirogallotti.com/' },
        { '@type': 'ListItem', position: 2, name: 'Collezione', item: 'https://cirogallotti.com/collezione' },
      ],
    },
  });

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [brands, setBrands] = useState([]);
  const [gemstones, setGemstones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [perPage, setPerPage] = useState(24);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');

  const productsSection = useScrollAnimation(0.1);
  const gridTopRef = useRef(null);

  const selectedCategory = searchParams.get('category') || '';
  const selectedMaterial = searchParams.get('material') || '';
  const selectedBrand = searchParams.get('brand') || '';
  const selectedGemstone = searchParams.get('gemstone') || '';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes, materialsRes, brandsRes, gemstonesRes] = await Promise.all([
          axios.get(`${API}/products?collection=Generale`),
          axios.get(`${API}/categories`),
          axios.get(`${API}/materials`),
          axios.get(`${API}/brands`),
          axios.get(`${API}/gemstones`),
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
        setMaterials(materialsRes.data);
        setBrands(brandsRes.data);
        setGemstones(gemstonesRes.data);
      } catch (e) {
        console.error('Error fetching data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredProducts = products.filter((p) => {
    if (selectedCategory && p.category !== selectedCategory) return false;
    if (selectedMaterial && p.material !== selectedMaterial) return false;
    if (selectedBrand && p.brand !== selectedBrand) return false;
    if (selectedGemstone && !(p.gemstones || []).some((g) => g.type === selectedGemstone)) return false;
    if (search) {
      const q = search.toLowerCase();
      const haystack = `${p.name} ${p.model_code || ''} ${p.material || ''} ${p.brand || ''} ${p.category || ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  }).sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

  // Paginazione
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / perPage));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * perPage;
  const paginatedProducts = filteredProducts.slice(pageStart, pageStart + perPage);

  // Torna a pagina 1 quando cambiano filtri o numero per pagina
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedMaterial, selectedBrand, selectedGemstone, perPage, search]);

  const goToPage = (page) => {
    const next = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(next);
    if (typeof window !== 'undefined' && gridTopRef.current) {
      // scroll in cima alla griglia, con offset per l'header fisso (~110px)
      const top = gridTopRef.current.getBoundingClientRect().top + window.scrollY - 110;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleCategoryChange = (category) => {
    const newParams = new URLSearchParams(searchParams);
    if (selectedCategory === category) {
      newParams.delete('category');
      newParams.delete('brand'); // brand ha senso solo con categoria Orologi
    } else {
      newParams.set('category', category);
      if (category !== 'Orologi') newParams.delete('brand');
    }
    setSearchParams(newParams);
  };

  const handleMaterialChange = (material) => {
    const newParams = new URLSearchParams(searchParams);
    if (selectedMaterial === material) newParams.delete('material');
    else newParams.set('material', material);
    setSearchParams(newParams);
  };

  const handleBrandChange = (brand) => {
    const newParams = new URLSearchParams(searchParams);
    if (selectedBrand === brand) newParams.delete('brand');
    else newParams.set('brand', brand);
    setSearchParams(newParams);
  };

  const handleGemstoneChange = (gemstone) => {
    const newParams = new URLSearchParams(searchParams);
    if (selectedGemstone === gemstone) newParams.delete('gemstone');
    else newParams.set('gemstone', gemstone);
    setSearchParams(newParams);
  };

  const clearFilters = () => { setSearchParams({}); setSearch(''); };
  const hasActiveFilters = selectedCategory || selectedMaterial || selectedBrand || selectedGemstone;
  const showBrandFilter = selectedCategory === 'Orologi';

  const FilterContent = () => (
    <div className="space-y-2">
      <Accordion type="multiple" defaultValue={['category', 'material', 'brand', 'gemstone']}>
        <AccordionItem value="category" className="border-line">
          <AccordionTrigger className="text-eyebrow text-ink hover:no-underline hover:text-bordeaux py-4">
            Categoria
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 pt-1 pb-4">
              {categories.map((category) => (
                <label
                  key={category}
                  className="flex items-center gap-3 cursor-pointer group"
                  data-testid={`filter-category-${category}`}
                >
                  <Checkbox
                    checked={selectedCategory === category}
                    onCheckedChange={() => handleCategoryChange(category)}
                    className="rounded-sm border-line data-[state=checked]:bg-bordeaux data-[state=checked]:border-bordeaux"
                  />
                  <span className="text-sm text-ink/75 group-hover:text-ink transition-colors">
                    {category}
                  </span>
                </label>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {showBrandFilter && (
          <AccordionItem value="brand" className="border-line">
            <AccordionTrigger className="text-eyebrow text-ink hover:no-underline hover:text-bordeaux py-4">
              Brand
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3 pt-1 pb-4">
                {brands.map((brand) => (
                  <label
                    key={brand}
                    className="flex items-center gap-3 cursor-pointer group"
                    data-testid={`filter-brand-${brand}`}
                  >
                    <Checkbox
                      checked={selectedBrand === brand}
                      onCheckedChange={() => handleBrandChange(brand)}
                      className="rounded-sm border-line data-[state=checked]:bg-bordeaux data-[state=checked]:border-bordeaux"
                    />
                    <span className="text-sm text-ink/75 group-hover:text-ink transition-colors">
                      {brand}
                    </span>
                  </label>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        <AccordionItem value="material" className="border-line">
          <AccordionTrigger className="text-eyebrow text-ink hover:no-underline hover:text-bordeaux py-4">
            Materiale
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 pt-1 pb-4">
              {materials.map((material) => (
                <label
                  key={material}
                  className="flex items-center gap-3 cursor-pointer group"
                  data-testid={`filter-material-${material}`}
                >
                  <Checkbox
                    checked={selectedMaterial === material}
                    onCheckedChange={() => handleMaterialChange(material)}
                    className="rounded-sm border-line data-[state=checked]:bg-bordeaux data-[state=checked]:border-bordeaux"
                  />
                  <span className="text-sm text-ink/75 group-hover:text-ink transition-colors">
                    {material}
                  </span>
                </label>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {gemstones.length > 0 && (
          <AccordionItem value="gemstone" className="border-line">
            <AccordionTrigger className="text-eyebrow text-ink hover:no-underline hover:text-bordeaux py-4">
              Pietra
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3 pt-1 pb-4">
                {gemstones.map((gemstone) => (
                  <label
                    key={gemstone}
                    className="flex items-center gap-3 cursor-pointer group"
                    data-testid={`filter-gemstone-${gemstone}`}
                  >
                    <Checkbox
                      checked={selectedGemstone === gemstone}
                      onCheckedChange={() => handleGemstoneChange(gemstone)}
                      className="rounded-sm border-line data-[state=checked]:bg-bordeaux data-[state=checked]:border-bordeaux"
                    />
                    <span className="text-sm text-ink/75 group-hover:text-ink transition-colors">
                      {gemstone}
                    </span>
                  </label>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>

      {hasActiveFilters && (
        <Button
          variant="outline"
          onClick={clearFilters}
          className="w-full mt-4"
          size="default"
          data-testid="clear-filters"
        >
          Rimuovi Filtri
        </Button>
      )}
    </div>
  );

  return (
    <div className="bg-ivory pt-24 md:pt-28" data-testid="catalog-page">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-16 py-16 md:py-20 hairline-b">
        <div className="max-w-container mx-auto">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-12 h-px bg-bordeaux" />
            <p className="text-eyebrow text-bordeaux">La Nostra Collezione</p>
          </div>
          <h1 className="font-display text-h1 text-ink">
            Gioielli <span className="italic text-ink/70">artigianali</span>
          </h1>
          <p className="mt-6 max-w-2xl text-ink/65 leading-relaxed">
            Ogni creazione è realizzata a mano nei nostri laboratori orafi di Napoli. Esplora la
            selezione completa di anelli, collane, bracciali e orecchini.
          </p>

          {/* Barra di ricerca */}
          <div className="mt-8 relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca un modello, materiale o brand…"
              className="pl-11 pr-10 h-12 rounded-full bg-ivory border-line"
              data-testid="catalog-search"
              aria-label="Cerca tra i gioielli"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center text-muted hover:text-ink hover:bg-line/60 transition-colors"
                data-testid="catalog-search-clear"
                aria-label="Cancella ricerca"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-container mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-28">
              <h2 className="text-eyebrow text-ink mb-6 flex items-center gap-3">
                <SlidersHorizontal className="w-4 h-4 text-bordeaux" />
                Filtra per
              </h2>
              <FilterContent />
            </div>
          </aside>

          {/* Mobile filter bar */}
          <div className="lg:hidden flex items-center justify-between">
            <p className="text-small text-muted">
              <span className="font-medium text-ink">{filteredProducts.length}</span> prodotti
            </p>
            <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" data-testid="mobile-filter-trigger">
                  <Filter className="w-4 h-4 mr-1" />
                  Filtri
                  {hasActiveFilters && (
                    <span className="ml-2 min-w-5 h-5 px-1 bg-bordeaux text-ivory text-[10px] flex items-center justify-center rounded-full">
                      {(selectedCategory ? 1 : 0) + (selectedMaterial ? 1 : 0) + (selectedBrand ? 1 : 0) + (selectedGemstone ? 1 : 0)}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-full sm:w-[360px] bg-ivory">
                <SheetTitle className="sr-only">Filtri prodotti</SheetTitle>
                <SheetDescription className="sr-only">
                  Filtra i gioielli per categoria, materiale e pietra.
                </SheetDescription>
                <div className="py-6">
                  <div className="flex items-center mb-8">
                    <h3 className="font-display text-h4">Filtri</h3>
                  </div>
                  <FilterContent />
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Products grid */}
          <div className="flex-1" ref={gridTopRef}>
            {hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-2 mb-8">
                <span className="text-eyebrow text-muted mr-2">Filtri attivi:</span>
                {selectedCategory && (
                  <button
                    onClick={() => handleCategoryChange(selectedCategory)}
                    className="flex items-center gap-2 px-3 py-1.5 border border-bordeaux text-bordeaux text-eyebrow hover:bg-bordeaux hover:text-ivory transition-colors duration-hover rounded-full"
                  >
                    {selectedCategory}
                    <X className="w-3 h-3" />
                  </button>
                )}
                {selectedBrand && (
                  <button
                    onClick={() => handleBrandChange(selectedBrand)}
                    className="flex items-center gap-2 px-3 py-1.5 border border-bordeaux text-bordeaux text-eyebrow hover:bg-bordeaux hover:text-ivory transition-colors duration-hover rounded-full"
                  >
                    {selectedBrand}
                    <X className="w-3 h-3" />
                  </button>
                )}
                {selectedMaterial && (
                  <button
                    onClick={() => handleMaterialChange(selectedMaterial)}
                    className="flex items-center gap-2 px-3 py-1.5 border border-bordeaux text-bordeaux text-eyebrow hover:bg-bordeaux hover:text-ivory transition-colors duration-hover rounded-full"
                  >
                    {selectedMaterial}
                    <X className="w-3 h-3" />
                  </button>
                )}
                {selectedGemstone && (
                  <button
                    onClick={() => handleGemstoneChange(selectedGemstone)}
                    className="flex items-center gap-2 px-3 py-1.5 border border-bordeaux text-bordeaux text-eyebrow hover:bg-bordeaux hover:text-ivory transition-colors duration-hover rounded-full"
                  >
                    {selectedGemstone}
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}

            <div ref={productsSection.ref}>
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="aspect-[3/4] bg-line rounded-md mb-5" />
                      <div className="h-3 bg-line w-20 mb-3" />
                      <div className="h-5 bg-line w-40 mb-2" />
                      <div className="h-4 bg-line w-24" />
                    </div>
                  ))}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-24">
                  <p className="text-muted mb-6 text-lg">Nessun prodotto trovato</p>
                  <Button onClick={clearFilters} variant="outline">
                    Rimuovi Filtri
                  </Button>
                </div>
              ) : (
                <>
                  {/* Barra risultati + per pagina */}
                  <div className="hidden lg:flex items-center justify-between mb-8">
                    <p className="text-small text-muted">
                      <span className="font-medium text-ink">{filteredProducts.length}</span> prodotti
                      {totalPages > 1 && (
                        <span className="text-muted"> · pagina {safePage} di {totalPages}</span>
                      )}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-eyebrow text-muted">Per pagina</span>
                      {[24, 48].map((n) => (
                        <button
                          key={n}
                          onClick={() => setPerPage(n)}
                          className={`px-3 py-1.5 text-eyebrow rounded-full border transition-colors duration-hover ${
                            perPage === n
                              ? 'border-bordeaux bg-bordeaux text-ivory'
                              : 'border-line text-ink/70 hover:border-ink'
                          }`}
                          data-testid={`per-page-${n}`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-12">
                    {paginatedProducts.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>

                  {/* Paginazione */}
                  {totalPages > 1 && (
                    <nav
                      className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 mt-16"
                      aria-label="Paginazione collezione"
                      data-testid="pagination"
                    >
                      <button
                        onClick={() => goToPage(safePage - 1)}
                        disabled={safePage === 1}
                        className="w-10 h-10 flex items-center justify-center rounded-full border border-line text-ink hover:border-ink hover:bg-ink hover:text-ivory transition-colors duration-hover disabled:opacity-40 disabled:pointer-events-none flex-shrink-0"
                        data-testid="pagination-prev"
                        aria-label="Pagina precedente"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      {getPageItems(safePage, totalPages).map((page, idx) =>
                        page === '…' ? (
                          <span
                            key={`dots-${idx}`}
                            className="w-8 h-10 flex items-center justify-center text-muted select-none"
                            aria-hidden="true"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => goToPage(page)}
                            aria-current={page === safePage ? 'page' : undefined}
                            className={`min-w-10 h-10 px-3 flex items-center justify-center rounded-full text-sm transition-colors duration-hover flex-shrink-0 ${
                              page === safePage
                                ? 'bg-bordeaux text-ivory'
                                : 'border border-line text-ink/70 hover:border-ink'
                            }`}
                            data-testid={`pagination-page-${page}`}
                          >
                            {page}
                          </button>
                        )
                      )}
                      <button
                        onClick={() => goToPage(safePage + 1)}
                        disabled={safePage === totalPages}
                        className="w-10 h-10 flex items-center justify-center rounded-full border border-line text-ink hover:border-ink hover:bg-ink hover:text-ivory transition-colors duration-hover disabled:opacity-40 disabled:pointer-events-none flex-shrink-0"
                        data-testid="pagination-next"
                        aria-label="Pagina successiva"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </nav>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
